// HKReturnLabelPanel.tsx
// 香港退貨 (順豐) — manual admin tool inside the OMS shipping page.
//
// Flow:
//   1. Admin enters the ORIGINAL HK order number → 查詢
//   2. We load the sender (the HK customer from that order) + items.
//      Receiver is fixed = Metanoia TW (from SF_SENDER_* env, server-side).
//   3. Admin can correct the HK sender fields, then 產生順豐退貨標籤
//   4. On success: show SF waybill no + 列印退貨標籤 (opens labelUrl)
//
// Reverse (HK→TW) waybill declares a low per-piece customs value server-side so the
// TW re-import 關稅 stays low. Manual-only — mirrors the outbound 香港出貨 gate.

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  AlertTitle,
  Divider,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  FlightTakeoff as FlightIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import api from '../services/api';
import { SF_REGIONS, SfCountryCode, isSfCountryCode, isIntlCountryCode, INTL_REGIONS } from '../config/intlRegions';

interface HKItem {
  id: number | string;
  productName: string;
  variantTitle?: string;
  sku?: string;
  quantity: number;
  price: number;
}

interface HKSender {
  contact: string;
  phoneNo: string;
  email: string;
  address: string;
  postCode?: string;
}

interface HKReceiver {
  contact: string;
  company?: string;
  phoneNo?: string;
  address?: string;
  region?: string;
}

interface WaybillResult {
  sfWaybillNo: string;
  labelUrl?: string | null;
  invoiceUrl?: string | null;
}

const HKReturnLabelPanel: React.FC = () => {
  // Destination region of the reverse (customer → Metanoia TW) waybill, resolved
  // from the looked-up order. Defaults to 'HK' so behavior for HK orders is
  // unchanged until the lookup response says otherwise.
  // SF-carried regions only — the reverse waybill is an SF product. A Parxl
  // region (SG) has no SF return lane at all; the backend throws by design.
  const [country, setCountry] = useState<SfCountryCode>('HK');
  const region = SF_REGIONS[country];

  const [orderNumber, setOrderNumber] = useState('');
  const [looking, setLooking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sender, setSender] = useState<HKSender | null>(null);
  const [receiver, setReceiver] = useState<HKReceiver | null>(null);
  const [items, setItems] = useState<HKItem[]>([]);
  const [result, setResult] = useState<WaybillResult | null>(null);
  const [loadedOrder, setLoadedOrder] = useState<string>('');

  const extractError = (e: any, fallback: string): string => {
    if (axios.isAxiosError(e)) return e.response?.data?.error || e.message || fallback;
    if (e instanceof Error) return e.message;
    return fallback;
  };

  const handleLookup = async () => {
    if (!orderNumber.trim() || looking) return;
    setLooking(true);
    setError(null);
    setResult(null);
    setSender(null);
    setReceiver(null);
    setItems([]);
    try {
      const resp = await api.post('/api/exchange-return/admin/hk-return/lookup', {
        orderNumber: orderNumber.trim(),
      });
      if (!resp.data?.success) throw new Error(resp.data?.error || '查詢失敗');
      const c = String(resp.data.country || resp.data.shippingCountry || '').toUpperCase();
      // A non-SF intl order (e.g. SG/Parxl) has no SF reverse waybill — stop here
      // instead of silently producing a Hong Kong return label for it.
      if (isIntlCountryCode(c) && !isSfCountryCode(c)) {
        throw new Error(
          `${INTL_REGIONS[c].nameZh}訂單由 ${INTL_REGIONS[c].carrierLabel} 配送，沒有順豐退貨單，請改用人工退貨流程`
        );
      }
      setCountry(isSfCountryCode(c) ? c : 'HK');
      setSender(resp.data.sender);
      setReceiver(resp.data.receiver);
      setItems(resp.data.items || []);
      setLoadedOrder(resp.data.orderNumber);
      if (resp.data.existing) {
        setResult({
          sfWaybillNo: resp.data.existing.sfWaybillNo,
          labelUrl: resp.data.existing.labelUrl,
        });
      }
    } catch (e: any) {
      setError(extractError(e, '查詢香港退貨訂單失敗'));
    } finally {
      setLooking(false);
    }
  };

  const handleSenderChange = (field: keyof HKSender) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSender((prev) => (prev ? { ...prev, [field]: e.target.value } : prev));
  };

  const handleCreate = async (force = false) => {
    if (!sender || creating) return;
    if (!sender.contact.trim()) { setError(`請輸入寄件人（${region.nameZh}客戶）姓名`); return; }
    if (!sender.phoneNo.trim()) { setError(`請輸入${region.nameZh}手機號碼`); return; }
    if (!sender.address.trim()) { setError(`請輸入${region.nameZh}地址`); return; }

    setCreating(true);
    setError(null);
    try {
      const region2 = region.guessRegionSecond(sender.address);
      // No item selection — the whole order is declared. Omitting `items` makes the
      // backend fall back to every line on the order.
      const resp = await api.post('/api/exchange-return/admin/hk-return/create-label', {
        orderNumber: loadedOrder,
        force,
        country,
        sender: {
          contact: sender.contact.trim(),
          phoneNo: sender.phoneNo.replace(/\D/g, ''),
          telNo: sender.phoneNo.replace(/\D/g, ''),
          phoneAreaCode: region.phoneAreaCode,
          telAreaCode: region.phoneAreaCode,
          email: sender.email.trim(),
          address: sender.address.trim(),
          postCode: sender.postCode || '999077',
          regionFirst: region.regionFirst,
          regionSecond: region2,
        },
      });
      if (!resp.data?.success) throw new Error(resp.data?.error || '產生標籤失敗');
      setResult({
        sfWaybillNo: resp.data.sfWaybillNo,
        labelUrl: resp.data.labelUrl,
        invoiceUrl: resp.data.invoiceUrl,
      });
    } catch (e: any) {
      setError(extractError(e, '產生香港退貨標籤失敗'));
    } finally {
      setCreating(false);
    }
  };

  const openLabel = () => {
    if (result?.labelUrl) window.open(result.labelUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>國際退貨 — 順豐反向標籤 ({region.code} → TW)</AlertTitle>
        輸入原始{region.nameZh}訂單編號，系統會帶出<strong>寄件人（{region.nameZh}客戶）</strong>資料，收件人固定為
        Metanoia（台灣倉）。確認後產生順豐退貨單，寄件人為{region.nameZh}客戶、收件人為你。退貨申報價值採低額（降低台灣進口關稅）。
      </Alert>

      {/* Order number lookup */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="原始訂單編號 Order Number"
              placeholder="ORD-XXXXX"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
              disabled={looking}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLookup}
              disabled={looking || !orderNumber.trim()}
              startIcon={looking ? <CircularProgress size={18} /> : <SearchIcon />}
            >
              {looking ? '查詢中...' : '查詢訂單'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Waybill result banner */}
      {result && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#e8f5e9' }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            順豐退貨單已建立
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <Box>
              <Typography variant="subtitle2">SF Waybill No</Typography>
              <Typography fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                {result.sfWaybillNo}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={openLabel}
              disabled={!result.labelUrl}
              sx={{ ml: 'auto' }}
            >
              列印退貨標籤
            </Button>
          </Box>
        </Paper>
      )}

      {/* Sender / receiver + items */}
      {sender && receiver && (
        <Grid container spacing={3}>
          {/* Sender (intl customer) — editable */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                寄件人（{region.nameZh}客戶）
                <Chip label={region.code} color="info" size="small" sx={{ ml: 1 }} />
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="姓名 Contact"
                    value={sender.contact} onChange={handleSenderChange('contact')} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label={`${region.nameZh}手機 (+${region.phoneAreaCode})`}
                    value={sender.phoneNo} onChange={handleSenderChange('phoneNo')} required
                    helperText={`${region.nameZh}手機 8 碼，免加 ${region.phoneAreaCode}`} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Email"
                    value={sender.email} onChange={handleSenderChange('email')} type="email" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label={`${region.nameZh}地址 Address`}
                    value={sender.address} onChange={handleSenderChange('address')}
                    multiline minRows={2} required
                    helperText="完整地址（樓層 / 室號 / 街名 / 大廈名）" />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Receiver (Metanoia TW) — fixed */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" gutterBottom>
                <HomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                收件人（Metanoia 台灣倉）
                <Chip label="固定" size="small" sx={{ ml: 1 }} />
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>{receiver.contact}</strong>
                {receiver.company ? ` · ${receiver.company}` : ''}
              </Typography>
              {receiver.phoneNo && (
                <Typography variant="body2" color="text.secondary">電話：{receiver.phoneNo}</Typography>
              )}
              {receiver.region && (
                <Typography variant="body2" color="text.secondary">{receiver.region}</Typography>
              )}
              {receiver.address && (
                <Typography variant="body2" color="text.secondary">{receiver.address}</Typography>
              )}
            </Paper>
          </Grid>

          {/* Items to return */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>退貨商品（整筆訂單退回）</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>商品</TableCell>
                    <TableCell>款式 / SKU</TableCell>
                    <TableCell align="center">數量</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">（無品項資料）</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((it) => (
                    <TableRow key={it.id} hover>
                      <TableCell>{it.productName}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{it.variantTitle || '—'}</Typography>
                        {it.sku && (
                          <Typography variant="caption" color="text.secondary">SKU: {it.sku}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">x {it.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                {result && (
                  <Button
                    variant="text"
                    color="warning"
                    onClick={() => handleCreate(true)}
                    disabled={creating}
                  >
                    重新產生（覆蓋）
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleCreate(false)}
                  disabled={creating || !!result}
                  startIcon={creating ? <CircularProgress size={18} /> : <FlightIcon />}
                >
                  {creating ? '產生中...' : result ? '已產生標籤' : '產生順豐退貨標籤'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default HKReturnLabelPanel;
